import { Trade } from './api';

// The trade store: trades per figi, ascending by `tradeSort`. Consumers read the tail
// (`useTrades` slices `-tradeCount`), so the most recent trades are at the end.
export type TradeStore = { [figi: string]: Trade[] };

// How many bonds' trade histories to retain, least-recently-touched evicted first. Purely a cache:
// the page displays one bond at a time and re-fetches on mount, so this only saves a round trip
// when navigating back. It matters more than it used to -- under the positional protocol every
// on-demand fetch answered `start: 0` ("replace everything"), so in practice it held little more
// than the bond being viewed. Bootstraps are upserts now, so it accumulates for real.
export const MAX_TRADE_FIGI_COUNT = 50;

// Rows retained per bond. A cap, not a target -- it must stay >= the largest window `useTrades`
// requests (`Math.max(...tradeCountOptions)` in app/bond/index.tsx, today exactly 100), or the
// store would silently retain less than was just fetched and the table would come up short with
// nothing logged. There is no headroom at equality: raise this if a larger trade count is offered.
export const MAX_TRADES_PER_FIGI = 100;

/**
 * A deterministic total order over trades, ascending.
 *
 * `entry` used to supply one server-side; under key addressing the client owns it
 * (DMM-839 protocol §7 rule 6). Report date is primary rather than the execution date the protocol
 * suggests, because that is the order this UI has always displayed and charted: `tradeChart` builds
 * its x-axis from report dates and assumes they ascend, and the server's `index: [-N...-1]` window
 * is itself report-date-ordered, so this is also what makes a `slice(-N)` of the store agree with
 * the rows a fetch returns. Execution date then `key` break ties, which is what makes the order
 * total -- trades in the same bond in the same second have no semantic order, the tiebreak only
 * needs to be stable.
 */
export const tradeSort = (a: Trade, b: Trade) =>
  a.report_date.getTime() - b.report_date.getTime() ||
  a.execution_date.getTime() - b.execution_date.getTime() ||
  (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);

/**
 * Apply one message's trades to the store: `store[key] = row` (DMM-839 protocol §7).
 *
 * Rows are absolute, not incremental -- `quantity` is the trade's total, already summed over every
 * report that collapsed into it -- so an incoming row *replaces* the row holding its key and is
 * never merged with it. That also makes the operation idempotent, which is what lets a bootstrap be
 * applied as an ordinary delta and a re-queued batch be replayed harmlessly.
 *
 * A figi's row count cannot shrink: the trim below runs on the merged union and only above the cap,
 * and two rows already held can never merge into one (the server admits one slot per key, so a
 * collapse only ever folds an *arriving* print into an existing row). `deleted` is the only channel
 * that can reduce it, and it is never sent today.
 */
export const mergeTrades = (existing: TradeStore, incoming: Trade[], deleted?: string[]): TradeStore => {
  const deletedKeys = deleted?.length ? new Set(deleted) : null;
  // Nothing to apply. Reached by `{"trade": []}`, which the server sends when a request found rows
  // but per-row containment dropped every one of them -- a client waiting on that request is
  // answered rather than left hanging. Returning the same object keeps consumers from re-rendering.
  // (A request whose window holds no trades at all does not come back this way: it is answered with
  // an `unrecognized figis` message and no trade payload.)
  if (!incoming.length && !deletedKeys) {
    return existing;
  }

  const incomingByFigi = new Map<string, Trade[]>();
  incoming.forEach(t => {
    const forFigi = incomingByFigi.get(t.figi);
    if (forFigi) {
      forFigi.push(t);
    } else {
      incomingByFigi.set(t.figi, [t]);
    }
  });

  // Touched figis first, so the cap evicts the least recently updated rather than an arbitrary set.
  // That relies on the store's key order being MRU, which holds because this function never mutates
  // the store in place: it rebuilds one below, assigning in exactly this order, so a touched figi is
  // re-seated at the front and the tail is genuinely the least recently updated. Enumeration order
  // is guaranteed for these keys -- own string properties come back in creation order, *except*
  // canonical array indices, which are hoisted ahead of them in numeric order. Figis ('BBG...') are
  // never that, but a store keyed by anything integer-like would silently lose this property, and
  // the eviction with it. Note "touched" means a message carried rows for the figi; reading the
  // store does not promote it.
  const figis = [
    ...incomingByFigi.keys(),
    ...Object.getOwnPropertyNames(existing).filter(figi => !incomingByFigi.has(figi))
  ].slice(0, MAX_TRADE_FIGI_COUNT);

  return figis.reduce((store: TradeStore, figi) => {
    const existingTrades = existing[figi] || [];
    const incomingTrades = incomingByFigi.get(figi);
    if (!incomingTrades && !deletedKeys) {
      // Untouched by this message. Deltas are filtered per subscribed figi now, so most figis take
      // this path on most messages; keep the same array so consumers of other bonds don't see a
      // change. Keys are opaque, so a deletion is the one case that has to visit every figi.
      if (existingTrades.length) {
        store[figi] = existingTrades;
      }
      return store;
    }

    const byKey = new Map(existingTrades.map(t => [t.key, t]));
    deletedKeys?.forEach(key => void byKey.delete(key));
    incomingTrades?.forEach(t => void byKey.set(t.key, t));
    if (!byKey.size) {
      return store;
    }

    const merged = Array.from(byKey.values()).sort(tradeSort);
    store[figi] = merged.length > MAX_TRADES_PER_FIGI ? merged.slice(-MAX_TRADES_PER_FIGI) : merged;
    return store;
  }, {});
};
